import { NavLink } from 'react-router';

import { cn } from '../../lib/cn.ts';
import { can, type Session } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { NAV, type NavItem } from './nav.ts';

const ICON = 'size-5 shrink-0';
const ROW = 'flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm';

/** Navigation shared by the desktop sidebar and the mobile sheet. */
export function SidebarNav({ session, onNavigate }: { session: Session; onNavigate?: () => void }) {
  const notify = useToast();
  return (
    <nav aria-label="Principal" className="space-y-6">
      {NAV.map((section) => {
        const visible = section.items.filter(
          (item) => !item.permission || can(session, item.permission),
        );
        const ready = visible.filter((item) => !item.comingSoon);
        const soon = visible.filter((item) => item.comingSoon);
        if (visible.length === 0) return null;
        return (
          <div key={section.title ?? 'main'} className="space-y-1">
            {section.title && <SectionTitle>{section.title}</SectionTitle>}
            <ul className="space-y-0.5">
              {ready.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    {...(onNavigate ? { onClick: onNavigate } : {})}
                    className={({ isActive }) =>
                      cn(
                        ROW,
                        'font-medium hover:bg-muted',
                        isActive && 'bg-soft text-soft-foreground hover:bg-soft',
                      )
                    }
                  >
                    <item.icon className={ICON} strokeWidth={1.75} aria-hidden="true" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            {soon.length > 0 && (
              <>
                <SectionTitle>Módulos en construcción</SectionTitle>
                <ul className="space-y-0.5">
                  {soon.map((item) => (
                    <li key={item.to}>
                      <ComingSoon
                        item={item}
                        onPress={() => {
                          notify(`${item.label}: próximamente.`, 'info');
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <p className="px-3 pt-3 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
      {children}
    </p>
  );
}

/**
 * A future module: dimmed and not navigable. Tapping it (no hover on a phone) says
 * "próximamente", so the app never looks unresponsive.
 */
function ComingSoon({ item, onPress }: { item: NavItem; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-disabled="true"
      title="Próximamente"
      onClick={onPress}
      className={cn(ROW, 'cursor-default text-muted-foreground hover:bg-muted/60')}
    >
      <item.icon className={cn(ICON, 'opacity-70')} strokeWidth={1.75} aria-hidden="true" />
      <span>
        {item.label}
        <span className="sr-only"> (próximamente)</span>
      </span>
    </button>
  );
}
